// Library Imports
import React, { useState, useEffect, useCallback } from 'react';
import { Document, Page } from 'react-pdf';
import { useDropzone } from 'react-dropzone';
import { pdfjs } from 'react-pdf';

// CSS Imports
import '../styles/pdf_uploaders.css';

// Configurations
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  '../../node_modules/react-pdf/node_modules/pdfjs-dist/build/pdf.worker.min.js',
  import.meta.url,
).toString();

/**
 * the container that allows the user to upload pdf and displayed them onto the screen
 * @returns {JSX.Element} rendered pdf uploader and displayed
 */
const PdfUploader = () => {
  const [pdfs, setPdfs] = useState([]);
  const [selectedPdfIndex, setSelectedPdfIndex] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [numPages, setNumPages] = useState(null);

  useEffect(() => {
    if (pdfs.length > 0) {
      UpdatePageNumbers(selectedPdfIndex, pdfs, setNumPages);
    }
  }, [pdfs, selectedPdfIndex]);

  return (
    <div className='pdf-uploader-container'>
      <FileDropBox pdfs={pdfs} setPdfs={setPdfs} />
      { pdfs.length > 0 && (
        <>
          <NavigationContainer currentPage={currentPage}  setCurrentPage={setCurrentPage}
            numPages={numPages} selectedPdfIndex={selectedPdfIndex} setSelectedPdfIndex={setSelectedPdfIndex}
            pdfs={pdfs} />
          <PdfDisplayContainer pdfs={pdfs} selectedPdfIndex={selectedPdfIndex}
            currentPage={currentPage} />
        </>
      )}
    </div>
  );
};

/**
 * container that allows the user to upload pdfs to be displayed on the screen
 * @param {Object} props - component props 
 * @param {Array} pdfs - an array of all uploaded pdfs
 * @param {function} setPdfs - function to update the array of pdfs 
 * @returns 
 */
function FileDropBox({pdfs, setPdfs}) { 
  const onDrop = (acceptedFiles) => {
    // Filter out duplicate files
    let newFiles = acceptedFiles.filter(file => !pdfs.some(pdf => pdf.name === file.name));
    newFiles = newFiles.filter(file => file.type === 'application/pdf');    
    
    // Update the state with new files
    setPdfs([...pdfs, ...newFiles]);
  };

  const { getRootProps, getInputProps } = useDropzone({
    accept: '.pdf',
    onDrop,
  });
  
  return (
    <div className='file-upload-box' {...getRootProps()} style={{}}>
        <img src="https://media.tenor.com/no0vOEEP1QMAAAAi/rain-bow-bird.gif" alt='bouncing-bird'/>
        <input {...getInputProps()} />
        <p>Drag & drop a PDF file here, or click to select one</p>
      </div>
  );
} 

/**
 * container that allows the user to navigate between different pdf files and pages
 * @param {Object} props - component props 
 * @param {number} currentPage - the current page of the selected pdf being rendered
 * @param {number} numPages - the number of pages that the pdf has
 * @param {number} selectedPdfIndex - the index of the selected pdf
 * @param {Array} pdfs - an array of all uploaded pdfs
 * @returns {JSX.Element} rendered container
 */
function NavigationContainer({currentPage, setCurrentPage, numPages, selectedPdfIndex, setSelectedPdfIndex, pdfs}) {
  const nextPage = useCallback(() => {
    if (currentPage < numPages) {
      setCurrentPage(currentPage + 1);
    }
  }, [currentPage, numPages, setCurrentPage]); // Dependencies for nextPage
  
  const prevPage = useCallback(() => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  }, [currentPage, setCurrentPage]); // Dependencies for prevPage
  

  const handlePdfSelect = (index) => {
    setSelectedPdfIndex(index);
    setCurrentPage(1);
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      switch (event.key) {
        case 'ArrowRight':
          nextPage();
          break;
        case 'ArrowLeft':
          prevPage();
          break;
        default:
          break;
      }
    };
  
    window.addEventListener('keydown', handleKeyDown);
  
    // Cleanup the event listener on component unmount
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [currentPage, numPages, nextPage, prevPage]); // Include nextPage and prevPage in dependencies if they're stable or memoized
  
  return (
    <div className='navigation-container'>
      <div className='pdf-selector-container'>
        <label>Select PDF:</label>
        <select className='pdf-selector' value={selectedPdfIndex} onChange={(e) => handlePdfSelect(e.target.value)}>
            {pdfs.map((pdf, index) => (
              <option key={index} value={index}>{pdf.name}</option>
            ))}
          </select>
      </div>
      <div className='page-navigation-container'>
        <button onClick={prevPage} disabled={currentPage === 1}>Previous</button>
        <h3>{` Page ${currentPage} of ${numPages || '-'} `}</h3>
        <button onClick={nextPage} disabled={currentPage === numPages}>Next</button>
      </div>
    </div>
  );
}

/**
 * containter that shows the current selected pdf onto the screen
 * @param {Object} props - component props
 * @param {Array} pdfs - an array of all uploaded pdfs
 * @param {number} selectedPdfIndex - index of the current selected pdf
 * @param {number} currentPage - the page number to be displayed
 * @returns {JSX.Element} rendered container 
 */
function PdfDisplayContainer({pdfs, selectedPdfIndex, currentPage }){
  return (
    <div className='pdf-display-container'>
      <Document file={pdfs[selectedPdfIndex]}>
        <Page pageNumber={currentPage} width={650} renderAnnotationLayer={false} renderTextLayer={false}/>
      </Document>
    </div>
  );
}

/**
 * update functions for changing the page of a selected pdf
 * @param {number} selectedPdfIndex - index of the current selected pdf
 * @param {Array} pdfs - an array of all uploaded pdfs
 * @param {number} setNumPages - used to set the page number for the selected pdf
 */
function UpdatePageNumbers(selectedPdfIndex, pdfs, setNumPages) {
  const selectedPdf = pdfs[selectedPdfIndex];
  const reader = new FileReader();
  reader.onload = (event) => {
    const typedArray = new Uint8Array(event.target.result);
    pdfjs.getDocument(typedArray).promise.then(
      pdf => {
        setNumPages(pdf.numPages);
      }
    );
  };
  reader.readAsArrayBuffer(selectedPdf);  
}

export default PdfUploader;